import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Avatar, Chip, Button, CircularProgress,
  Alert, List, ListItemButton, ListItemAvatar, ListItemText, Divider,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import axios from 'axios';
import { API_URL, uploadMyPhoto, uploadPersonPhoto } from '../api';

const EDITOR_ROLES = ['ADMIN', 'PRESIDENT', 'ELDER'];

// Cloudinary face-centered thumbnail
const photoThumb = (url, size = 200) =>
  url && typeof url === 'string' && url.includes('/upload/')
    ? url.replace('/upload/', `/upload/w_${size},h_${size},c_fill,g_face/`)
    : url;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

const idOf = (ref) => (ref && typeof ref === 'object' ? ref._id : ref);

const ProfilePage = ({ currentUser }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [person, setPerson] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const [pRes, meRes] = await Promise.all([
        axios.get(`${API_URL}/api/people/${id}`),
        axios.get(`${API_URL}/api/auth/me`),
      ]);
      setPerson(pRes.data);
      setMe(meRes.data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load this profile.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (!person) {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
        <Alert severity="error">{error || 'Person not found.'}</Alert>
      </Box>
    );
  }

  const viewerPersonId = idOf(me?.person);
  const isSelf = viewerPersonId && String(viewerPersonId) === String(person._id);
  const isEditor = EDITOR_ROLES.includes(me?.role || currentUser?.role);
  const canChangePhoto = isSelf || isEditor;

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setSuccess('');
    setUploading(true);
    try {
      const data = isSelf
        ? await uploadMyPhoto(file)
        : await uploadPersonPhoto(person._id, file);
      setSuccess(data.message);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Photo upload failed. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Family sections
  const parents = [
    person.mother && { ...(typeof person.mother === 'object' ? person.mother : { _id: person.mother }), _label: 'Mother' },
    person.father && { ...(typeof person.father === 'object' ? person.father : { _id: person.father }), _label: 'Father' },
    person.socialParent && { ...(typeof person.socialParent === 'object' ? person.socialParent : { _id: person.socialParent }), _label: 'Social parent' },
  ].filter(Boolean);

  const spouses = (person.spouses || []).filter((s) => s && idOf(s));
  const kawetos = (person.kawetos || []).filter((k) => k && idOf(k));
  const children = (person.children || []).filter((c) => c && idOf(c));

  const PersonRow = ({ p, label }) => (
    <ListItemButton onClick={() => navigate(`/person/${idOf(p)}`)} disabled={!p.name}>
      <ListItemAvatar>
        <Avatar src={p.photoPath ? photoThumb(p.photoPath, 64) : undefined} alt={p.name || ''}>
          {(p.name || '?').charAt(0)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={p.name || 'Family member'}
        secondary={label}
        primaryTypographyProps={{ fontWeight: 600 }}
      />
    </ListItemButton>
  );

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Card sx={{ borderRadius: 3, mb: 2, overflow: 'visible' }}>
        <Box sx={{
          height: 84, borderRadius: '12px 12px 0 0',
          background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)',
        }} />
        <CardContent sx={{ pt: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mt: -6, flexWrap: 'wrap' }}>
            <Avatar
              src={person.photoPath ? photoThumb(person.photoPath, 300) : undefined}
              alt={person.name}
              sx={{ width: 104, height: 104, border: '4px solid #fff', fontSize: 40, bgcolor: '#065F46' }}
            >
              {person.name.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 200, pb: 0.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{person.name}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                <Chip size="small" label={person.gender === 'MALE' ? '♂ Male' : person.gender === 'FEMALE' ? '♀ Female' : 'Other'} />
                <Chip size="small" label={person.status === 'DEAD' ? 'Rests with the ancestors' : 'Alive'}
                  color={person.status === 'DEAD' ? 'default' : 'success'} variant="outlined" />
                {person.role === 'KAWETO' && <Chip size="small" label="Kaweto" color="secondary" />}
                {fmtDate(person.birthDate) && <Chip size="small" label={`Born ${fmtDate(person.birthDate)}`} variant="outlined" />}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            {viewerPersonId && !isSelf && (
              <Button
                variant="contained"
                startIcon={<FamilyRestroomIcon />}
                onClick={() => navigate(`/tree?rel1=${viewerPersonId}&rel2=${person._id}`)}
                sx={{ background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)' }}
              >
                How are we related?
              </Button>
            )}
            <Button variant="outlined" startIcon={<AccountTreeIcon />} onClick={() => navigate('/tree')}>
              Open the tree
            </Button>
            {canChangePhoto && (
              <Button
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
                disabled={uploading}
                onClick={() => fileInput.current?.click()}
              >
                {person.photoPath ? 'Change photo' : 'Add photo'}
              </Button>
            )}
            <input type="file" accept="image/*" hidden ref={fileInput} onChange={handlePhoto} />
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Family */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Family</Typography>

          {parents.length > 0 && (
            <>
              <Typography variant="overline" color="text.secondary">Parents</Typography>
              <List dense disablePadding>
                {parents.map((p) => <PersonRow key={`${idOf(p)}-${p._label}`} p={p} label={p._label} />)}
              </List>
            </>
          )}

          {(spouses.length > 0 || kawetos.length > 0) && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="overline" color="text.secondary">
                Spouse{spouses.length + kawetos.length === 1 ? '' : 's'}
              </Typography>
              <List dense disablePadding>
                {spouses.map((p) => <PersonRow key={idOf(p)} p={typeof p === 'object' ? p : { _id: p }} label="Spouse" />)}
                {kawetos.map((p) => <PersonRow key={idOf(p)} p={typeof p === 'object' ? p : { _id: p }} label="Kaweto" />)}
              </List>
            </>
          )}

          {children.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="overline" color="text.secondary">
                Children ({children.length})
              </Typography>
              <List dense disablePadding>
                {children.map((p) => <PersonRow key={idOf(p)} p={typeof p === 'object' ? p : { _id: p }} label="" />)}
              </List>
            </>
          )}

          {parents.length === 0 && spouses.length === 0 && kawetos.length === 0 && children.length === 0 && (
            <Typography variant="body2" color="text.secondary">No family connections recorded yet.</Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProfilePage;
